<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useWindowSize } from '@vueuse/core';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useProductCatalogLauncher } from 'dashboard/composables/useProductCatalogLauncher';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import wootConstants from 'dashboard/constants/globals';
import ProductCatalogApi from 'dashboard/api/integrations/productCatalog';
import PosOrdersApi from 'dashboard/api/posOrders';
import PixPaymentsApi from 'dashboard/api/pixPayments';
import MercadoPagoApi from 'dashboard/api/integrations/mercadoPago';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  chat: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const {
  pendingProduct,
  pendingCatalogAction,
  consumePendingProduct,
  consumePendingCatalogAction,
} = useProductCatalogLauncher();

const activeTab = ref('catalog');
const isLoading = ref(false);
const isSending = ref(false);
const isSendingSummary = ref(false);
const isClosingOrder = ref(false);
const cartStep = ref('cart');
const isMercadoPagoConnected = ref(false);
const isGeneratingPayment = ref(false);
const isSendingPayment = ref(false);
const paymentResult = ref(null);
const catalog = ref({});
const products = ref([]);
const contactOrders = ref([]);
const selectedProductIds = ref([]);
const searchQuery = ref('');
const sourceFilter = ref('all');
const selectedProduct = ref(null);
const selectedVariationId = ref(null);
const selectedImageIndex = ref(0);
const cart = ref([]);
const surchargeInput = ref(0);
const shippingInput = ref(0);
const discountInput = ref(0);
const discountMode = ref('percent');
const isLoadingOrders = ref(false);
const deferredCatalogAction = ref(null);
const isCalculatorOpen = ref(false);
const calculatorExpression = ref('');
const calculatorKeys = [
  '7',
  '8',
  '9',
  '÷',
  '4',
  '5',
  '6',
  '×',
  '1',
  '2',
  '3',
  '-',
  'C',
  '0',
  '.',
  '+',
];

const currentUser = computed(() => store.getters.getCurrentUser);

const availabilityLabels = computed(() => ({
  in_stock: t('CONVERSATION.PRODUCT_CATALOG.AVAILABILITY.IN_STOCK'),
  out_of_stock: t('CONVERSATION.PRODUCT_CATALOG.AVAILABILITY.OUT_OF_STOCK'),
}));

const sourceOptions = [
  { value: 'all', label: 'Todos canais' },
  { value: 'olist', label: 'Olist' },
  { value: 'feed_xml', label: 'Feed' },
  { value: 'local', label: 'Catálogo local' },
];
const ordersTabLabel = 'Pedidos';
const contactOrdersTitle = 'Pedidos do contato';
const loadingOrdersLabel = 'Carregando pedidos...';
const emptyOrdersLabel = 'Nenhum pedido encontrado para este contato.';
const orderNumberPrefix = 'Pedido #';
const quantitySeparator = 'x ';

const normalizedSource = product => {
  if (product?.source === 'olist') return 'olist';
  if (product?.source === 'feed_xml' || product?.source === 'external') {
    return 'feed_xml';
  }
  return 'local';
};

const sourceLabel = product =>
  sourceOptions.find(option => option.value === normalizedSource(product))
    ?.label || 'Catálogo local';

const sourceBadgeClass = product =>
  ({
    olist: 'bg-purple-50 text-purple-700 ring-purple-100',
    feed_xml: 'bg-amber-50 text-amber-700 ring-amber-100',
    local: 'bg-blue-50 text-blue-700 ring-blue-100',
  })[normalizedSource(product)];

const filteredProducts = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const source = sourceFilter.value;
  const sourceFiltered =
    source === 'all'
      ? products.value
      : products.value.filter(product => normalizedSource(product) === source);
  if (!query) return sourceFiltered;

  return sourceFiltered.filter(product =>
    [product.name, product.description, product.brand]
      .filter(Boolean)
      .some(value => value.toLowerCase().includes(query))
  );
});

const selectedProducts = computed(() =>
  filteredProducts.value.filter(product =>
    selectedProductIds.value.includes(product.id)
  )
);

const canSend = computed(
  () => selectedProducts.value.length > 0 && !isSending.value
);
const canAddToCart = computed(() => selectedProducts.value.length > 0);
const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
const productVariations = computed(
  () => selectedProduct.value?.variations || []
);
const hasVariations = computed(() => productVariations.value.length > 1);
const selectedVariation = computed(
  () =>
    productVariations.value.find(
      variation => variation.id === selectedVariationId.value
    ) ||
    productVariations.value.find(
      variation => variation.availability !== 'out_of_stock'
    ) ||
    productVariations.value[0] ||
    null
);
const variationLabel = variation =>
  [variation.size, variation.color].filter(Boolean).join(' · ') ||
  t('CONVERSATION.PRODUCT_CATALOG.VARIATION_DEFAULT');

// The catalog list shows one card per item_group_id; picking a variation
// (size/color) swaps in that variant's id/price/image before cart/send.
const effectiveProduct = computed(() => {
  if (!selectedProduct.value) return null;
  if (!selectedVariation.value) return selectedProduct.value;

  return {
    ...selectedProduct.value,
    id: selectedVariation.value.id,
    retailer_id: selectedVariation.value.retailer_id,
    price: selectedVariation.value.price ?? selectedProduct.value.price,
    currency:
      selectedVariation.value.currency ?? selectedProduct.value.currency,
    availability:
      selectedVariation.value.availability ??
      selectedProduct.value.availability,
    image_url:
      selectedVariation.value.image_url ?? selectedProduct.value.image_url,
    url: selectedVariation.value.url ?? selectedProduct.value.url,
  };
});

const selectedProductImages = computed(() => {
  const baseImages = selectedProduct.value?.images || [];
  const variationImage = selectedVariation.value?.image_url;
  if (variationImage && !baseImages.includes(variationImage)) {
    return [variationImage, ...baseImages];
  }
  return baseImages;
});
const selectedProductImage = computed(
  () =>
    selectedProductImages.value[selectedImageIndex.value] ||
    effectiveProduct.value?.image_url
);

const cartQuantity = computed(() =>
  cart.value.reduce((total, item) => total + item.quantity, 0)
);
const cartCurrency = computed(() => cart.value[0]?.currency || 'BRL');
const cartSubtotalCents = computed(() =>
  Math.round(
    cart.value.reduce(
      (total, item) => total + item.priceCents * item.quantity,
      0
    )
  )
);
const surchargeCents = computed(() =>
  Math.round((Number(surchargeInput.value) || 0) * 100)
);
const shippingCents = computed(() =>
  Math.round((Number(shippingInput.value) || 0) * 100)
);
const discountCents = computed(() => {
  const value = Number(discountInput.value) || 0;
  if (!value) return 0;

  return discountMode.value === 'percent'
    ? Math.round((cartSubtotalCents.value * value) / 100)
    : Math.round(value * 100);
});
const hasCartAdjustments = computed(
  () =>
    surchargeCents.value > 0 ||
    shippingCents.value > 0 ||
    discountCents.value > 0
);
const cartTotalCents = computed(() =>
  Math.max(
    cartSubtotalCents.value +
      surchargeCents.value +
      shippingCents.value -
      discountCents.value,
    0
  )
);
const canCloseOrder = computed(
  () => cart.value.length > 0 && !isClosingOrder.value
);
const canSendSummary = computed(
  () => cart.value.length > 0 && !isSendingSummary.value
);
const contactId = computed(() => props.chat.meta?.sender?.id);

const closeCatalogPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_olist_panel_open: false,
    is_product_catalog_panel_open: false,
  });
  emit('close');
};

const closeCatalogPanelOnSmallScreen = () => {
  if (isSmallScreen.value && uiSettings.value?.is_product_catalog_panel_open) {
    closeCatalogPanel();
  }
};

const fetchCatalog = async () => {
  isLoading.value = true;
  try {
    const { data } = await ProductCatalogApi.get();
    catalog.value = data.catalog || {};
    products.value = data.products || [];
    selectedProductIds.value = [];
    selectedProduct.value = null;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.PRODUCT_CATALOG.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const fetchContactOrders = async () => {
  if (!contactId.value) {
    contactOrders.value = [];
    return;
  }

  isLoadingOrders.value = true;
  try {
    const { data } = await PosOrdersApi.list({ contact_id: contactId.value });
    contactOrders.value = data.payload || data.pos_orders || [];
  } catch (error) {
    contactOrders.value = [];
  } finally {
    isLoadingOrders.value = false;
  }
};

const productPayload = product => ({
  id: product.id,
  retailer_id: product.retailer_id || product.id,
  name: product.name,
  description: product.description,
  price: product.price,
  currency: product.currency,
  image_url: product.image_url,
  images: product.images,
  availability: product.availability,
  brand: product.brand,
  collections: product.collections,
  url: product.url,
  source: normalizedSource(product),
  catalog_item_id: product.catalog_item_id,
});

const sendProductsToCustomer = async (productsToSend, onSuccess) => {
  if (!productsToSend.length || isSending.value) return;
  isSending.value = true;

  try {
    const {
      data: { messages = [] },
    } = await ProductCatalogApi.send({
      conversationId: props.chat.id,
      catalog: catalog.value,
      products: productsToSend.map(productPayload),
    });
    messages.forEach(message => store.dispatch('addMessage', message));
    onSuccess?.();
    useAlert(t('CONVERSATION.PRODUCT_CATALOG.SEND_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.PRODUCT_CATALOG.SEND_ERROR')
    );
  } finally {
    isSending.value = false;
  }
};

const sendCatalog = () => {
  if (!canSend.value) return;
  const sentIds = selectedProducts.value.map(product => product.id);
  sendProductsToCustomer(selectedProducts.value, () => {
    selectedProductIds.value = selectedProductIds.value.filter(
      id => !sentIds.includes(id)
    );
  });
};

const sendSingleProductToCustomer = product => {
  sendProductsToCustomer([product]);
};

const toggleProductSelection = productId => {
  const index = selectedProductIds.value.indexOf(productId);
  if (index === -1) {
    selectedProductIds.value.push(productId);
  } else {
    selectedProductIds.value.splice(index, 1);
  }
};

const openProduct = product => {
  selectedProduct.value = product;
  selectedVariationId.value = null;
  selectedImageIndex.value = 0;
};

const closeProduct = () => {
  selectedProduct.value = null;
};

const selectVariation = variation => {
  selectedVariationId.value = variation.id;
  selectedImageIndex.value = 0;
};

const showPreviousImage = () => {
  if (!selectedProductImages.value.length) return;
  selectedImageIndex.value =
    selectedImageIndex.value === 0
      ? selectedProductImages.value.length - 1
      : selectedImageIndex.value - 1;
};

const showNextImage = () => {
  if (!selectedProductImages.value.length) return;
  selectedImageIndex.value =
    (selectedImageIndex.value + 1) % selectedProductImages.value.length;
};

const availabilityLabel = availability => {
  if (!availability) return '';
  return availabilityLabels.value[availability] || availability;
};

// Product prices coming from the catalog integration are formatted strings
// (e.g. "R$ 19,90"), the cart/PDV backend works in integer cents.
const priceStringToCents = value => {
  if (!value) return 0;
  const numeric = String(value).replace(/[^0-9,.]/g, '');
  const normalized = numeric.includes(',')
    ? numeric.replace(/\./g, '').replace(',', '.')
    : numeric;
  return Math.round(Number(normalized || 0) * 100);
};

const formatCurrency = (cents, currency = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(
    (cents || 0) / 100
  );

const formatQuantity = quantity => String(quantity ?? 1).replace('.', ',');

const formatDate = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const statusLabel = status =>
  ({
    draft: 'Rascunho',
    open: 'Aberto',
    completed: 'Concluído',
    cancelled: 'Cancelado',
  })[status] || status;

const paymentStatusLabel = status =>
  ({
    unpaid: 'Não pago',
    pending: 'Pendente',
    paid: 'Pago',
    failed: 'Falhou',
    refunded: 'Estornado',
    expired: 'Expirado',
  })[status] || status;

const orderItemThumbnail = item =>
  item?.thumbnail || item?.metadata?.external_product?.image_url;

// Renders `valueRef` as plain, formatted text (e.g. "R$ 0,00") at rest, and
// switches to a bare editable number while focused, so the field never looks
// like a boxed input until the user actually interacts with it.
const useEditableAmount = (valueRef, formatFn) => {
  const text = ref(formatFn());

  watch(formatFn, formatted => {
    text.value = formatted;
  });

  const onFocus = event => {
    text.value = valueRef.value ? String(valueRef.value).replace('.', ',') : '';
    nextTick(() => event.target?.select());
  };

  const onBlur = () => {
    const numeric =
      Number(
        String(text.value)
          .replace(/[^0-9,.-]/g, '')
          .replace(',', '.')
      ) || 0;
    valueRef.value = numeric;
    text.value = formatFn();
  };

  return { text, onFocus, onBlur };
};

const {
  text: surchargeText,
  onFocus: onSurchargeFocus,
  onBlur: onSurchargeBlur,
} = useEditableAmount(surchargeInput, () =>
  formatCurrency(surchargeCents.value, cartCurrency.value)
);
const {
  text: shippingText,
  onFocus: onShippingFocus,
  onBlur: onShippingBlur,
} = useEditableAmount(shippingInput, () =>
  formatCurrency(shippingCents.value, cartCurrency.value)
);
const {
  text: discountText,
  onFocus: onDiscountFocus,
  onBlur: onDiscountBlur,
} = useEditableAmount(discountInput, () =>
  discountMode.value === 'percent'
    ? `${discountInput.value || 0}%`
    : formatCurrency(discountCents.value, cartCurrency.value)
);

const addProductToCart = product => {
  const existing = cart.value.find(item => item.id === product.id);
  if (existing) {
    existing.quantity += 1;
    return;
  }

  cart.value.push({
    id: product.id,
    catalogItemId: product.catalog_item_id || null,
    name: product.name,
    description: product.description,
    priceCents: priceStringToCents(product.price),
    currency: product.currency || 'BRL',
    thumbnail: product.image_url,
    quantity: 1,
    metadata: {
      external_product: productPayload(product),
      source: normalizedSource(product),
    },
  });
};

const addCustomProductToCart = () => {
  cart.value.push({
    id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    catalogItemId: null,
    isCustom: true,
    name: t('CONVERSATION.PRODUCT_CATALOG.CUSTOM_PRODUCT_DEFAULT_NAME'),
    description: '',
    priceCents: 0,
    currency: cartCurrency.value || 'BRL',
    thumbnail: null,
    quantity: 1,
    metadata: { source: 'custom' },
  });
};

const findProductByTerm = term => {
  const normalizeSearchText = value =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const normalizedTerm = normalizeSearchText(term);
  if (!normalizedTerm) return null;

  const searchTokens = normalizedTerm
    .split(' ')
    .map(token => token.replace(/s$/, ''))
    .filter(Boolean);

  return products.value.find(product =>
    [product.name, product.description, product.brand, product.retailer_id]
      .filter(Boolean)
      .some(value => {
        const normalizedValue = normalizeSearchText(value);
        return (
          normalizedValue.includes(normalizedTerm) ||
          searchTokens.every(token => normalizedValue.includes(token))
        );
      })
  );
};

const bestCatalogSearch = term => {
  const normalizeSearchText = value =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const ignoredTokens = new Set([
    'ola',
    'olá',
    'bom',
    'dia',
    'boa',
    'tarde',
    'noite',
    'gostaria',
    'queria',
    'quero',
    'preciso',
    'saber',
    'mais',
    'informacoes',
    'informações',
    'sobre',
    'isso',
    'esse',
    'essa',
    'produto',
    'valor',
    'preco',
    'preço',
    'orcamento',
    'orçamento',
    'tem',
    'voces',
    'vocês',
    'por',
    'favor',
    'para',
    'com',
    'de',
    'do',
    'da',
    'um',
    'uma',
    'o',
    'a',
  ]);
  const tokens = normalizeSearchText(term)
    .split(' ')
    .filter(token => token.length > 1 && !ignoredTokens.has(token));
  if (!tokens.length) return String(term || '').trim();

  const match = products.value
    .map(product => {
      const searchable = normalizeSearchText(
        [product.name, product.description, product.brand]
          .filter(Boolean)
          .join(' ')
      );
      return {
        product,
        score: tokens.reduce(
          (total, token) => total + (searchable.includes(token) ? 1 : 0),
          0
        ),
      };
    })
    .filter(({ score }) => score > 0)
    .sort((left, right) => right.score - left.score)[0];

  return match?.product?.name || tokens.join(' ');
};

const addProductToCartWithQuantity = (product, quantity = 1) => {
  const beforeQuantity = cartQuantity.value;
  addProductToCart(product);
  const item = cart.value.find(record => record.id === product.id);
  if (item && quantity > 1 && cartQuantity.value > beforeQuantity) {
    item.quantity = quantity;
  }
};

const addSelectedToCart = () => {
  if (!canAddToCart.value) return;
  selectedProducts.value.forEach(addProductToCart);
  useAlert(t('CONVERSATION.PRODUCT_CATALOG.CART_ADD_SUCCESS'));
};

const addSingleProductToCart = product => {
  addProductToCart(product);
  useAlert(t('CONVERSATION.PRODUCT_CATALOG.CART_ADD_SUCCESS'));
};

const updateCartQuantity = (itemId, delta) => {
  const item = cart.value.find(record => record.id === itemId);
  if (!item) return;
  item.quantity = Math.max(
    0.01,
    Math.round((item.quantity + delta) * 100) / 100
  );
};

const setCartQuantity = (item, value) => {
  const parsed = Number(String(value).replace(',', '.'));
  item.quantity = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const onCartPriceFocus = (event, item) => {
  event.target.value = (item.priceCents / 100).toString().replace('.', ',');
  nextTick(() => event.target.select());
};

const onCartPriceBlur = (event, item) => {
  const numeric =
    Number(
      String(event.target.value)
        .replace(/[^0-9,.-]/g, '')
        .replace(',', '.')
    ) || 0;
  item.priceCents = Math.max(0, Math.round(numeric * 100));
  event.target.value = formatCurrency(item.priceCents, item.currency);
};

const removeFromCart = itemId => {
  cart.value = cart.value.filter(item => item.id !== itemId);
};

const cartSummaryText = () => {
  const lines = cart.value.map(
    item =>
      `${formatQuantity(item.quantity)}x ${item.name} — ${formatCurrency(
        Math.round(item.priceCents * item.quantity),
        item.currency
      )}`
  );
  lines.push('');

  if (hasCartAdjustments.value) {
    lines.push(
      `${t('CONVERSATION.PRODUCT_CATALOG.CART_SUBTOTAL')}: ${formatCurrency(
        cartSubtotalCents.value,
        cartCurrency.value
      )}`
    );
    if (surchargeCents.value > 0) {
      lines.push(
        `${t('CONVERSATION.PRODUCT_CATALOG.CART_SURCHARGE')}: ${formatCurrency(
          surchargeCents.value,
          cartCurrency.value
        )}`
      );
    }
    if (shippingCents.value > 0) {
      lines.push(
        `${t('CONVERSATION.PRODUCT_CATALOG.CART_SHIPPING')}: ${formatCurrency(
          shippingCents.value,
          cartCurrency.value
        )}`
      );
    }
    if (discountCents.value > 0) {
      lines.push(
        `${t('CONVERSATION.PRODUCT_CATALOG.CART_DISCOUNT')}: -${formatCurrency(
          discountCents.value,
          cartCurrency.value
        )}`
      );
    }
  }

  lines.push(
    `${t('CONVERSATION.PRODUCT_CATALOG.CART_TOTAL')}: ${formatCurrency(
      cartTotalCents.value,
      cartCurrency.value
    )}`
  );
  return lines.join('\n');
};

// Only digits/operators reach the Function constructor below, so there is
// no arbitrary code execution risk despite evaluating a user-typed string.
const evaluateCalculatorExpression = expression => {
  if (!/^[0-9+\-*/.,() ]+$/.test(expression)) return null;

  try {
    // eslint-disable-next-line no-new-func
    const result = Function(
      `"use strict"; return (${expression.replace(/,/g, '.')})`
    )();
    return Number.isFinite(result) ? result : null;
  } catch (error) {
    return null;
  }
};

const calculatorResult = computed(() =>
  evaluateCalculatorExpression(calculatorExpression.value)
);

const openCalculator = () => {
  calculatorExpression.value = '';
  isCalculatorOpen.value = true;
};

const closeCalculator = () => {
  isCalculatorOpen.value = false;
};

const pressCalculatorKey = key => {
  if (key === 'C') {
    calculatorExpression.value = '';
    return;
  }
  if (key === '⌫') {
    calculatorExpression.value = calculatorExpression.value.slice(0, -1);
    return;
  }
  const symbol = { '÷': '/', '×': '*' }[key] || key;
  calculatorExpression.value += symbol;
};

const addCustomProductFromCalculator = () => {
  if (!calculatorResult.value) return;

  addCustomProductToCart();
  const item = cart.value[cart.value.length - 1];
  item.priceCents = Math.max(0, Math.round(calculatorResult.value * 100));
  closeCalculator();
  useAlert(t('CONVERSATION.PRODUCT_CATALOG.CART_ADD_SUCCESS'));
};

const sendCartSummary = async () => {
  if (!canSendSummary.value) return;
  isSendingSummary.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.chat.id,
      message: cartSummaryText(),
      private: false,
      sender: {
        name: currentUser.value?.name,
        thumbnail: currentUser.value?.avatar_url,
      },
    });
    useAlert(t('CONVERSATION.PRODUCT_CATALOG.SEND_SUMMARY_SUCCESS'));
  } finally {
    isSendingSummary.value = false;
  }
};

const fetchMercadoPagoStatus = async () => {
  try {
    const { data } = await MercadoPagoApi.status();
    isMercadoPagoConnected.value = !!data.connected;
  } catch (error) {
    isMercadoPagoConnected.value = false;
  }
};

const orderDescription = () =>
  t('CONVERSATION.PRODUCT_CATALOG.CART_ORDER_DESCRIPTION', {
    contact: props.chat.meta?.sender?.name || '',
  });

const buildPosOrderPayload = () => ({
  conversation_id: props.chat.id,
  contact_id: props.chat.meta?.sender?.id,
  currency: cartCurrency.value,
  status: 'open',
  discount_cents: discountCents.value,
  shipping_cents: shippingCents.value,
  surcharge_cents: surchargeCents.value,
  pos_order_items_attributes: cart.value.map(item => ({
    catalog_item_id: item.catalogItemId,
    name: item.name,
    description: item.description,
    quantity: item.quantity,
    unit_price_cents: item.priceCents,
    currency: item.currency,
    metadata: item.metadata || {},
  })),
});

const goToPaymentMethod = () => {
  if (!canCloseOrder.value) return;
  cartStep.value = 'payment-method';
};

const backToCart = () => {
  cartStep.value = 'cart';
};

const generatePixKeyPayment = async posOrderId => {
  const { data } = await PixPaymentsApi.create({
    amountCents: cartTotalCents.value,
    description: orderDescription(),
  });

  await PosOrdersApi.update(posOrderId, {
    payment_provider: 'pix_key',
    payment_method: 'pix',
    payment_qr_code: data.payload,
  });

  return {
    method: 'pix_key',
    code: data.payload,
    qrImage: await QRCode.toDataURL(data.payload, { width: 240, margin: 1 }),
  };
};

const generateMercadoPagoPayment = async (posOrderId, kind) => {
  const { data } = await MercadoPagoApi.createPayment({
    kind,
    amountCents: cartTotalCents.value,
    description: orderDescription(),
    conversationId: props.chat.id,
    contactId: props.chat.meta?.sender?.id,
    contactName: props.chat.meta?.sender?.name,
    contactEmail: props.chat.meta?.sender?.email,
    posOrderId,
  });

  if (kind === 'pix') {
    return {
      method: 'mp_pix',
      code: data.qr_code,
      qrImage: data.qr_code
        ? await QRCode.toDataURL(data.qr_code, { width: 240, margin: 1 })
        : null,
    };
  }

  return { method: 'mp_link', checkoutUrl: data.checkout_url };
};

const selectPaymentMethod = async method => {
  if (isGeneratingPayment.value) return;
  isGeneratingPayment.value = true;

  try {
    const { data } = await PosOrdersApi.create(buildPosOrderPayload());
    const posOrderId = data.payload.id;

    const result =
      method === 'pix_key'
        ? await generatePixKeyPayment(posOrderId)
        : await generateMercadoPagoPayment(
            posOrderId,
            method === 'mp_pix' ? 'pix' : 'payment_link'
          );

    paymentResult.value = { ...result, posOrderId };
    cartStep.value = 'payment-result';
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.PRODUCT_CATALOG.CLOSE_ORDER_ERROR')
    );
  } finally {
    isGeneratingPayment.value = false;
  }
};

const copyPaymentCode = async () => {
  const text = paymentResult.value?.checkoutUrl || paymentResult.value?.code;
  if (!text) return;

  try {
    await copyTextToClipboard(text);
    useAlert(t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_CODE_COPIED'));
  } catch (error) {
    useAlert(t('CONVERSATION.PRODUCT_CATALOG.CLOSE_ORDER_ERROR'));
  }
};

const sendPaymentToCustomer = async () => {
  const text = paymentResult.value?.checkoutUrl || paymentResult.value?.code;
  if (!text || isSendingPayment.value) return;
  isSendingPayment.value = true;

  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.chat.id,
      message: text,
      private: false,
      sender: {
        name: currentUser.value?.name,
        thumbnail: currentUser.value?.avatar_url,
      },
    });
    useAlert(t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_SENT_SUCCESS'));
  } finally {
    isSendingPayment.value = false;
  }
};

const resetCartAfterCheckout = () => {
  cart.value = [];
  surchargeInput.value = 0;
  shippingInput.value = 0;
  discountInput.value = 0;
  paymentResult.value = null;
  cartStep.value = 'cart';
};

const markOrderAsPaid = async () => {
  if (!paymentResult.value?.posOrderId || isClosingOrder.value) return;
  isClosingOrder.value = true;

  try {
    await PosOrdersApi.complete(paymentResult.value.posOrderId);
    useAlert(t('CONVERSATION.PRODUCT_CATALOG.CLOSE_ORDER_SUCCESS'));
    resetCartAfterCheckout();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.PRODUCT_CATALOG.CLOSE_ORDER_ERROR')
    );
  } finally {
    isClosingOrder.value = false;
  }
};

// Keeps the cart (and which screen of it, e.g. payment method/result) around
// per conversation, so switching conversations and coming back lands exactly
// where the agent left off instead of resetting to the catalog list.
const cartStorageKey = LOCAL_STORAGE_KEYS.PRODUCT_CATALOG_CART;
let isRestoringCartState = false;

const persistCartState = () => {
  if (!props.chat.id || isRestoringCartState) return;

  LocalStorage.updateJsonStore(cartStorageKey, props.chat.id, {
    activeTab: activeTab.value,
    cartStep: cartStep.value,
    cart: cart.value,
    surcharge: surchargeInput.value,
    shipping: shippingInput.value,
    discount: discountInput.value,
    discountMode: discountMode.value,
    paymentResult: paymentResult.value,
  });
};

const restoreCartState = () => {
  if (!props.chat.id) return;
  const saved = LocalStorage.getFromJsonStore(cartStorageKey, props.chat.id);

  isRestoringCartState = true;
  activeTab.value = saved?.activeTab || 'catalog';
  cartStep.value = saved?.cartStep || 'cart';
  cart.value = saved?.cart || [];
  surchargeInput.value = saved?.surcharge || 0;
  shippingInput.value = saved?.shipping || 0;
  discountInput.value = saved?.discount || 0;
  discountMode.value = saved?.discountMode || 'percent';
  paymentResult.value = saved?.paymentResult || null;
  nextTick(() => {
    isRestoringCartState = false;
  });
};

watch(
  [
    cart,
    surchargeInput,
    shippingInput,
    discountInput,
    discountMode,
    activeTab,
    cartStep,
    paymentResult,
  ],
  persistCartState,
  { deep: true }
);

// When a product card in the chat is clicked, it stashes the product here
// and asks this panel to open; pick it up whether that happened before or
// after this component mounted.
const applyPendingProduct = () => {
  const product = consumePendingProduct();
  if (!product) return;

  activeTab.value = 'catalog';
  const loadedMatch = products.value.find(
    candidate =>
      candidate.id === product.id ||
      (product.retailer_id && candidate.retailer_id === product.retailer_id)
  );
  openProduct(loadedMatch || product);
};

function applyPendingCatalogAction(actionToApply) {
  const action = actionToApply || consumePendingCatalogAction();
  if (!action) return;

  const requestedSearch = action.search || action.search_query || action.text;
  // The customer message is often a full sentence. Search with the closest
  // catalog product name instead, so desktop and mobile get the same useful
  // prefilled result.
  const search = requestedSearch ? bestCatalogSearch(requestedSearch) : '';
  if (search) {
    activeTab.value = 'catalog';
    searchQuery.value = search;
  }

  if (action.open_orders) {
    activeTab.value = 'orders';
    fetchContactOrders();
    return;
  }

  const requestedItems = Array.isArray(action.items) ? action.items : [];
  const needsProducts =
    requestedItems.length || (action.prepare_cart && search);
  if (needsProducts && !products.value.length) {
    deferredCatalogAction.value = action;
    return;
  }

  if (requestedItems.length) {
    const matchedProducts = requestedItems
      .map(item => ({
        product: findProductByTerm(item.name || item.query || item.sku),
        quantity: Number(item.quantity) || 1,
      }))
      .filter(item => item.product);

    if (matchedProducts.length) {
      matchedProducts.forEach(item =>
        addProductToCartWithQuantity(item.product, item.quantity)
      );
      activeTab.value = 'cart';
      useAlert('Carrinho preparado com os produtos encontrados.');
      return;
    }
  }

  if (action.prepare_cart && search) {
    const matchedProduct = findProductByTerm(search);
    if (matchedProduct) {
      addProductToCart(matchedProduct);
      activeTab.value = 'cart';
      useAlert('Carrinho preparado com o produto encontrado.');
    }
  }
}

watch(pendingProduct, product => {
  if (product) applyPendingProduct();
});

watch(pendingCatalogAction, action => {
  if (action) applyPendingCatalogAction();
});

watch(products, loadedProducts => {
  if (!loadedProducts.length || !deferredCatalogAction.value) return;

  const action = deferredCatalogAction.value;
  deferredCatalogAction.value = null;
  applyPendingCatalogAction(action);
});

watch(
  () => props.chat.id,
  () => {
    fetchCatalog();
    fetchContactOrders();
    restoreCartState();
  },
  { immediate: true }
);

onMounted(() => {
  fetchMercadoPagoStatus();
  applyPendingProduct();
  applyPendingCatalogAction();
});
</script>

<template>
  <aside
    v-on-click-outside="() => closeCatalogPanelOnSmallScreen()"
    class="bg-n-background h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <div
      class="flex h-12 shrink-0 items-center justify-between border-b border-n-weak px-3"
    >
      <div class="inline-flex gap-1 rounded-full bg-n-alpha-1 p-1">
        <button
          type="button"
          class="rounded-full px-3 py-1 text-xs font-medium transition-colors"
          :class="
            activeTab === 'catalog'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="activeTab = 'catalog'"
        >
          {{ t('CONVERSATION.PRODUCT_CATALOG.CATALOG_TAB') }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors"
          :class="
            activeTab === 'cart'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="activeTab = 'cart'"
        >
          {{ t('CONVERSATION.PRODUCT_CATALOG.CART_TAB') }}
          <span
            v-if="cartQuantity"
            class="grid size-4 place-content-center rounded-full bg-n-blue-9 text-[10px] font-semibold text-white"
          >
            {{ cartQuantity }}
          </span>
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors"
          :class="
            activeTab === 'orders'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="
            activeTab = 'orders';
            fetchContactOrders();
          "
        >
          {{ ordersTabLabel }}
          <span
            v-if="contactOrders.length"
            class="grid size-4 place-content-center rounded-full bg-n-blue-9 text-[10px] font-semibold text-white"
          >
            {{ contactOrders.length }}
          </span>
        </button>
      </div>
      <Button
        v-tooltip="t('GENERAL.CLOSE')"
        icon="i-lucide-x"
        ghost
        sm
        @click="closeCatalogPanel"
      />
    </div>

    <template v-if="activeTab === 'catalog'">
      <div
        v-if="!selectedProduct"
        class="flex flex-col gap-2 border-b border-n-weak px-4 py-3"
      >
        <input
          v-model="searchQuery"
          class="reset-base h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none"
          type="search"
          :placeholder="t('CONVERSATION.PRODUCT_CATALOG.SEARCH_PLACEHOLDER')"
        />
        <label
          class="flex h-9 items-center gap-2 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-xs text-n-slate-11"
        >
          <span class="i-lucide-filter size-3.5" />
          <select
            v-model="sourceFilter"
            class="reset-base min-w-0 flex-1 cursor-pointer bg-transparent p-0 text-xs font-medium text-n-slate-12 outline-none"
          >
            <option
              v-for="option in sourceOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>

      <div v-if="selectedProduct" class="flex min-h-0 flex-1 flex-col">
        <div class="shrink-0 border-b border-n-weak px-4 py-3">
          <Button
            :label="t('CONVERSATION.PRODUCT_CATALOG.BACK')"
            slate
            link
            sm
            @click="closeProduct"
          />
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <div
            class="relative mb-4 grid aspect-square place-items-center overflow-hidden rounded-md bg-n-alpha-1"
          >
            <img
              v-if="selectedProductImage"
              :src="selectedProductImage"
              :alt="selectedProduct.name"
              class="h-full w-full object-cover"
            />
            <div
              v-if="selectedProductImages.length > 1"
              class="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-2"
            >
              <button
                class="grid size-8 place-items-center rounded-full bg-n-solid-1/90 text-n-slate-12"
                type="button"
                @click="showPreviousImage"
              >
                <span class="i-lucide-chevron-left size-4" />
              </button>
              <button
                class="grid size-8 place-items-center rounded-full bg-n-solid-1/90 text-n-slate-12"
                type="button"
                @click="showNextImage"
              >
                <span class="i-lucide-chevron-right size-4" />
              </button>
            </div>
          </div>
          <h3 class="mb-2 text-base font-semibold text-n-slate-12">
            {{ selectedProduct.name }}
          </h3>
          <div class="mb-3 flex flex-wrap gap-2 text-xs">
            <span
              class="rounded-md px-2 py-1 font-semibold ring-1"
              :class="sourceBadgeClass(selectedProduct)"
            >
              {{ sourceLabel(selectedProduct) }}
            </span>
            <span
              v-if="effectiveProduct.price"
              class="rounded-md bg-n-alpha-2 px-2 py-1 font-medium text-n-slate-12"
            >
              {{ effectiveProduct.currency }} {{ effectiveProduct.price }}
            </span>
            <span
              v-if="effectiveProduct.availability"
              class="rounded-md bg-n-alpha-2 px-2 py-1 text-n-slate-11"
            >
              {{ availabilityLabel(effectiveProduct.availability) }}
            </span>
            <span
              v-for="collection in selectedProduct.collections"
              :key="collection"
              class="rounded-md bg-n-alpha-2 px-2 py-1 text-n-slate-11"
            >
              {{ collection }}
            </span>
          </div>
          <div v-if="hasVariations" class="mb-3">
            <p class="mb-1.5 text-xs font-medium text-n-slate-11">
              {{ t('CONVERSATION.PRODUCT_CATALOG.VARIATIONS_TITLE') }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="variation in productVariations"
                :key="variation.id"
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium ring-1 transition-colors"
                :class="[
                  variation.id === selectedVariation?.id
                    ? 'bg-n-blue-9 text-white ring-n-blue-9'
                    : 'bg-n-alpha-1 text-n-slate-12 ring-n-weak hover:bg-n-alpha-2',
                  variation.availability === 'out_of_stock' && 'opacity-50',
                ]"
                @click="selectVariation(variation)"
              >
                {{ variationLabel(variation) }}
              </button>
            </div>
          </div>
          <p
            v-if="selectedProduct.description"
            class="whitespace-pre-line text-sm leading-6 text-n-slate-11"
          >
            {{ selectedProduct.description }}
          </p>
          <a
            v-if="effectiveProduct.url"
            class="mt-4 inline-block text-sm font-medium text-n-blue-11 underline"
            :href="effectiveProduct.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t('CONVERSATION.PRODUCT_CATALOG.OPEN_PRODUCT') }}
          </a>
        </div>
        <div class="flex shrink-0 flex-col gap-2 border-t border-n-weak p-3">
          <Button
            :label="t('CONVERSATION.PRODUCT_CATALOG.SEND_TO_CUSTOMER')"
            slate
            faded
            sm
            class="w-full"
            :disabled="isSending"
            @click="sendSingleProductToCustomer(effectiveProduct)"
          />
          <Button
            :label="t('CONVERSATION.PRODUCT_CATALOG.ADD_TO_CART')"
            blue
            sm
            class="w-full"
            @click="addSingleProductToCart(effectiveProduct)"
          />
        </div>
      </div>

      <div
        v-else-if="isLoading"
        class="grid flex-1 place-items-center text-sm text-n-slate-11"
      >
        {{ t('CONVERSATION.PRODUCT_CATALOG.LOADING') }}
      </div>
      <div
        v-else-if="!filteredProducts.length"
        class="grid flex-1 place-items-center px-6 text-center text-sm text-n-slate-11"
      >
        {{ t('CONVERSATION.PRODUCT_CATALOG.EMPTY') }}
      </div>
      <ul v-else class="min-h-0 flex-1 overflow-y-auto">
        <li
          v-for="product in filteredProducts"
          :key="product.id"
          class="flex cursor-pointer items-center gap-3 border-b border-n-weak px-4 py-3 last:border-b-0 hover:bg-n-alpha-1"
          @click="openProduct(product)"
        >
          <Checkbox
            :model-value="selectedProductIds.includes(product.id)"
            @click.stop
            @update:model-value="toggleProductSelection(product.id)"
          />
          <img
            v-if="product.image_url"
            :src="product.image_url"
            :alt="product.name"
            class="size-12 rounded-md object-cover"
          />
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-n-slate-12">
              {{ product.name }}
            </p>
            <span
              class="mt-1 inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold ring-1"
              :class="sourceBadgeClass(product)"
            >
              {{ sourceLabel(product) }}
            </span>
            <span
              v-if="product.variation_count > 1"
              class="mt-1 ml-1 inline-flex rounded-md bg-n-alpha-1 px-1.5 py-0.5 text-[10px] font-semibold text-n-slate-11 ring-1 ring-n-weak"
            >
              {{
                t('CONVERSATION.PRODUCT_CATALOG.VARIATIONS_COUNT', {
                  count: product.variation_count,
                })
              }}
            </span>
            <p
              v-if="product.description"
              class="mt-1 line-clamp-2 text-xs text-n-slate-11"
            >
              {{ product.description }}
            </p>
            <p
              v-if="product.price"
              class="mt-1 text-xs font-medium text-n-slate-12"
            >
              {{ product.currency }} {{ product.price }}
            </p>
            <p v-if="product.availability" class="mt-1 text-xs text-n-slate-11">
              {{ availabilityLabel(product.availability) }}
            </p>
          </div>
        </li>
      </ul>

      <div
        v-if="!selectedProduct"
        class="flex shrink-0 flex-col gap-2 border-t border-n-weak p-3"
      >
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.SEND_SELECTED')"
          slate
          faded
          sm
          class="w-full"
          :disabled="!canSend"
          @click="sendCatalog"
        />
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.ADD_TO_CART')"
          blue
          sm
          class="w-full"
          :disabled="!canAddToCart"
          @click="addSelectedToCart"
        />
      </div>
    </template>

    <template v-else-if="activeTab === 'orders'">
      <div
        class="flex shrink-0 items-center justify-between border-b border-n-weak px-4 py-3"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ contactOrdersTitle }}
        </p>
        <Button
          v-tooltip="'Recarregar'"
          icon="i-lucide-refresh-cw"
          ghost
          xs
          :disabled="isLoadingOrders"
          @click="fetchContactOrders"
        />
      </div>

      <div
        v-if="isLoadingOrders"
        class="grid flex-1 place-items-center text-sm text-n-slate-11"
      >
        {{ loadingOrdersLabel }}
      </div>
      <div
        v-else-if="!contactOrders.length"
        class="grid flex-1 place-items-center px-6 text-center text-sm text-n-slate-11"
      >
        {{ emptyOrdersLabel }}
      </div>
      <ul v-else class="min-h-0 flex-1 overflow-y-auto">
        <li
          v-for="order in contactOrders"
          :key="order.id"
          class="border-b border-n-weak px-4 py-3 last:border-b-0"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-n-slate-12">
                {{ orderNumberPrefix }}{{ order.external_number || order.id }}
              </p>
              <p class="mt-1 text-xs text-n-slate-11">
                {{ formatDate(order.created_at) }}
              </p>
            </div>
            <p class="shrink-0 text-sm font-semibold text-n-slate-12">
              {{ formatCurrency(order.total_cents, order.currency || 'BRL') }}
            </p>
          </div>
          <div class="mt-2 flex flex-wrap gap-1.5">
            <span
              class="rounded-full bg-n-alpha-1 px-2 py-0.5 text-[11px] font-medium text-n-slate-11"
            >
              {{ statusLabel(order.status) }}
            </span>
            <span
              class="rounded-full bg-n-alpha-1 px-2 py-0.5 text-[11px] font-medium text-n-slate-11"
            >
              {{ paymentStatusLabel(order.payment_status) }}
            </span>
            <span
              v-if="order.external_source"
              class="rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-500/10 dark:text-purple-200"
            >
              {{ order.external_source }}
            </span>
          </div>
          <div
            v-if="order.items?.length"
            class="mt-3 flex gap-2 overflow-hidden"
          >
            <div
              v-for="item in order.items.slice(0, 3)"
              :key="item.id"
              class="min-w-0 flex flex-1 items-center gap-2 rounded-lg bg-n-alpha-1 p-2"
            >
              <img
                v-if="orderItemThumbnail(item)"
                :src="orderItemThumbnail(item)"
                :alt="item.name"
                class="size-8 shrink-0 rounded object-cover"
              />
              <span
                v-else
                class="grid size-8 shrink-0 place-content-center rounded bg-n-alpha-2 text-n-slate-10"
              >
                <span class="i-lucide-package size-4" />
              </span>
              <p class="truncate text-xs text-n-slate-11">
                {{ item.quantity }}{{ quantitySeparator }}{{ item.name }}
              </p>
            </div>
          </div>
        </li>
      </ul>
    </template>

    <template v-else-if="activeTab === 'cart' && cartStep === 'cart'">
      <div
        class="flex shrink-0 items-center gap-2 border-b border-n-weak px-3 py-2"
      >
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.CUSTOM_PRODUCT_BUTTON')"
          icon="i-lucide-plus"
          slate
          faded
          xs
          @click="addCustomProductToCart"
        />
        <Button
          v-tooltip="t('CONVERSATION.PRODUCT_CATALOG.CALCULATOR_OPEN')"
          icon="i-lucide-calculator"
          slate
          faded
          xs
          @click="openCalculator"
        />
      </div>
      <div
        v-if="!cart.length"
        class="grid flex-1 place-items-center px-6 text-center text-sm text-n-slate-11"
      >
        {{ t('CONVERSATION.PRODUCT_CATALOG.CART_EMPTY') }}
      </div>
      <ul v-else class="min-h-0 flex-1 overflow-y-auto">
        <li
          v-for="item in cart"
          :key="item.id"
          class="flex gap-3 border-b border-n-weak px-4 py-3 last:border-b-0"
        >
          <img
            v-if="item.thumbnail"
            :src="item.thumbnail"
            :alt="item.name"
            class="size-12 shrink-0 rounded-md object-cover"
          />
          <span
            v-else
            class="grid size-12 shrink-0 place-content-center rounded-md bg-n-alpha-1 text-n-slate-10"
          >
            <span class="i-lucide-package size-5" />
          </span>
          <div class="min-w-0 flex-1">
            <input
              v-if="item.isCustom"
              v-model="item.name"
              type="text"
              class="reset-base w-full border-0 bg-transparent p-0 text-sm font-medium text-n-slate-12 outline-none focus:rounded focus:ring-1 focus:ring-n-blue-8"
              :placeholder="
                t(
                  'CONVERSATION.PRODUCT_CATALOG.CUSTOM_PRODUCT_NAME_PLACEHOLDER'
                )
              "
            />
            <p v-else class="truncate text-sm font-medium text-n-slate-12">
              {{ item.name }}
            </p>
            <input
              type="text"
              inputmode="decimal"
              class="reset-base mt-1 w-24 border-0 bg-transparent p-0 text-left text-xs text-n-slate-11 outline-none focus:rounded focus:ring-1 focus:ring-n-blue-8"
              :value="formatCurrency(item.priceCents, item.currency)"
              @focus="event => onCartPriceFocus(event, item)"
              @blur="event => onCartPriceBlur(event, item)"
            />
            <div class="mt-2 flex items-center gap-2">
              <button
                class="grid size-6 place-content-center rounded-full bg-n-alpha-1 text-n-slate-11"
                type="button"
                @click="updateCartQuantity(item.id, -1)"
              >
                <span class="i-lucide-minus size-3" />
              </button>
              <input
                type="text"
                inputmode="decimal"
                class="reset-base h-6 w-14 rounded-md border border-n-weak bg-n-alpha-1 p-0 text-center text-xs font-medium text-n-slate-12 outline-none"
                :value="formatQuantity(item.quantity)"
                @change="event => setCartQuantity(item, event.target.value)"
              />
              <button
                class="grid size-6 place-content-center rounded-full bg-n-alpha-1 text-n-slate-11"
                type="button"
                @click="updateCartQuantity(item.id, 1)"
              >
                <span class="i-lucide-plus size-3" />
              </button>
              <button
                class="ml-auto grid size-6 place-content-center rounded-full text-n-ruby-9"
                type="button"
                @click="removeFromCart(item.id)"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
          <p class="shrink-0 self-start text-sm font-semibold text-n-slate-12">
            {{
              formatCurrency(
                Math.round(item.priceCents * item.quantity),
                item.currency
              )
            }}
          </p>
        </li>
      </ul>

      <div class="flex shrink-0 flex-col gap-2 border-t border-n-weak p-3">
        <div class="flex items-center justify-between text-sm">
          <span class="text-n-slate-11">
            {{ t('CONVERSATION.PRODUCT_CATALOG.CART_SUBTOTAL') }}
          </span>
          <span class="text-n-slate-12">
            {{ formatCurrency(cartSubtotalCents, cartCurrency) }}
          </span>
        </div>

        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.PRODUCT_CATALOG.CART_SURCHARGE') }}
          </span>
          <input
            v-model="surchargeText"
            type="text"
            inputmode="decimal"
            class="reset-base w-24 border-0 bg-transparent p-0 text-right text-xs text-n-slate-12 outline-none focus:rounded focus:ring-1 focus:ring-n-blue-8"
            @focus="onSurchargeFocus"
            @blur="onSurchargeBlur"
          />
        </div>

        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.PRODUCT_CATALOG.CART_SHIPPING') }}
          </span>
          <input
            v-model="shippingText"
            type="text"
            inputmode="decimal"
            class="reset-base w-24 border-0 bg-transparent p-0 text-right text-xs text-n-slate-12 outline-none focus:rounded focus:ring-1 focus:ring-n-blue-8"
            @focus="onShippingFocus"
            @blur="onShippingBlur"
          />
        </div>

        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.PRODUCT_CATALOG.CART_DISCOUNT') }}
          </span>
          <div class="flex items-center gap-1">
            <div class="inline-flex rounded-md bg-n-alpha-1 p-0.5">
              <button
                type="button"
                class="rounded px-1.5 py-0.5 text-[11px] font-medium"
                :class="
                  discountMode === 'percent'
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11'
                "
                @click="discountMode = 'percent'"
              >
                %
              </button>
              <button
                type="button"
                class="rounded px-1.5 py-0.5 text-[11px] font-medium"
                :class="
                  discountMode === 'fixed'
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11'
                "
                @click="discountMode = 'fixed'"
              >
                {{ t('CONVERSATION.PRODUCT_CATALOG.CURRENCY_LABEL') }}
              </button>
            </div>
            <input
              v-model="discountText"
              type="text"
              inputmode="decimal"
              class="reset-base w-20 border-0 bg-transparent p-0 text-right text-xs text-n-slate-12 outline-none focus:rounded focus:ring-1 focus:ring-n-blue-8"
              @focus="onDiscountFocus"
              @blur="onDiscountBlur"
            />
          </div>
        </div>

        <div
          class="flex items-center justify-between border-t border-n-weak pt-2 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONVERSATION.PRODUCT_CATALOG.CART_TOTAL') }}
          </span>
          <span class="text-base font-semibold text-n-slate-12">
            {{ formatCurrency(cartTotalCents, cartCurrency) }}
          </span>
        </div>
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.SEND_SUMMARY')"
          slate
          faded
          sm
          class="w-full"
          :disabled="!canSendSummary"
          @click="sendCartSummary"
        />
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.CLOSE_ORDER')"
          blue
          sm
          class="w-full"
          :disabled="!canCloseOrder"
          @click="goToPaymentMethod"
        />
      </div>
    </template>

    <template v-else-if="activeTab === 'cart' && cartStep === 'payment-method'">
      <div class="border-b border-n-weak px-4 py-3">
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.BACK_TO_CART')"
          slate
          link
          sm
          @click="backToCart"
        />
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto p-3">
        <p class="mb-3 px-1 text-xs text-n-slate-11">
          {{ t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_METHOD_TITLE') }}
        </p>
        <div class="flex flex-col gap-2">
          <button
            type="button"
            class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-left transition-colors hover:bg-n-alpha-1 disabled:cursor-wait disabled:opacity-50"
            :disabled="isGeneratingPayment"
            @click="selectPaymentMethod('pix_key')"
          >
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_METHODS.PIX_KEY') }}
              </span>
              <span class="block text-xs text-n-slate-11">
                {{
                  t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_METHODS.PIX_KEY_HELP')
                }}
              </span>
            </span>
          </button>

          <button
            v-if="isMercadoPagoConnected"
            type="button"
            class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-left transition-colors hover:bg-n-alpha-1 disabled:cursor-wait disabled:opacity-50"
            :disabled="isGeneratingPayment"
            @click="selectPaymentMethod('mp_pix')"
          >
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_METHODS.MP_PIX') }}
              </span>
              <span class="block text-xs text-n-slate-11">
                {{
                  t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_METHODS.MP_PIX_HELP')
                }}
              </span>
            </span>
          </button>

          <button
            v-if="isMercadoPagoConnected"
            type="button"
            class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-left transition-colors hover:bg-n-alpha-1 disabled:cursor-wait disabled:opacity-50"
            :disabled="isGeneratingPayment"
            @click="selectPaymentMethod('mp_link')"
          >
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_METHODS.MP_LINK') }}
              </span>
              <span class="block text-xs text-n-slate-11">
                {{
                  t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_METHODS.MP_LINK_HELP')
                }}
              </span>
            </span>
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="activeTab === 'cart'">
      <div class="border-b border-n-weak px-4 py-3">
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.BACK_TO_CART')"
          slate
          link
          sm
          @click="backToCart"
        />
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto p-4">
        <div v-if="paymentResult?.qrImage" class="mb-4 flex justify-center">
          <img
            :src="paymentResult.qrImage"
            alt="QR code"
            class="size-48 rounded-lg border border-n-weak"
          />
        </div>
        <div v-if="paymentResult?.code" class="mb-3">
          <p class="mb-1 text-xs text-n-slate-11">
            {{ t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_CODE_LABEL') }}
          </p>
          <p
            class="break-all rounded-md border border-n-weak bg-n-alpha-1 p-2 text-xs text-n-slate-12"
          >
            {{ paymentResult.code }}
          </p>
        </div>
        <div v-if="paymentResult?.checkoutUrl" class="mb-3">
          <p class="mb-1 text-xs text-n-slate-11">
            {{ t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_LINK_LABEL') }}
          </p>
          <a
            class="block break-all rounded-md border border-n-weak bg-n-alpha-1 p-2 text-xs text-n-blue-11 underline"
            :href="paymentResult.checkoutUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ paymentResult.checkoutUrl }}
          </a>
        </div>
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_COPY_CODE')"
          slate
          faded
          sm
          class="w-full"
          @click="copyPaymentCode"
        />
      </div>
      <div class="flex shrink-0 flex-col gap-2 border-t border-n-weak p-3">
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_SEND_TO_CUSTOMER')"
          slate
          faded
          sm
          class="w-full"
          :disabled="isSendingPayment"
          @click="sendPaymentToCustomer"
        />
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.PAYMENT_MARK_AS_PAID')"
          blue
          sm
          class="w-full"
          :disabled="isClosingOrder"
          @click="markOrderAsPaid"
        />
      </div>
    </template>

    <div
      v-if="isCalculatorOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      @click.self="closeCalculator"
    >
      <div class="w-full max-w-xs rounded-lg bg-n-solid-1 p-4 shadow-lg">
        <div class="mb-3 flex items-center justify-between">
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('CONVERSATION.PRODUCT_CATALOG.CALCULATOR_TITLE') }}
          </p>
          <Button icon="i-lucide-x" ghost xs @click="closeCalculator" />
        </div>
        <div class="mb-3 rounded-md bg-n-alpha-1 px-3 py-3 text-right">
          <p class="min-h-5 truncate text-sm text-n-slate-11">
            {{ calculatorExpression || '0' }}
          </p>
          <p class="truncate text-xl font-semibold text-n-slate-12">
            {{
              calculatorResult !== null
                ? formatCurrency(Math.round(calculatorResult * 100))
                : '—'
            }}
          </p>
        </div>
        <div class="grid grid-cols-4 gap-2">
          <button
            v-for="key in calculatorKeys"
            :key="key"
            type="button"
            class="rounded-md bg-n-alpha-1 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            @click="pressCalculatorKey(key)"
          >
            {{ key }}
          </button>
          <button
            type="button"
            class="col-span-4 grid place-items-center rounded-md bg-n-alpha-1 py-2 text-n-slate-11 hover:bg-n-alpha-2"
            @click="pressCalculatorKey('⌫')"
          >
            <span class="i-lucide-delete size-4" />
          </button>
        </div>
        <Button
          :label="t('CONVERSATION.PRODUCT_CATALOG.CALCULATOR_ADD_PRODUCT')"
          blue
          sm
          class="mt-3 w-full"
          :disabled="!calculatorResult"
          @click="addCustomProductFromCalculator"
        />
      </div>
    </div>
  </aside>
</template>
